import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Box,
  Typography,
  Modal,
  Backdrop,
  Fade,
  IconButton,
  Checkbox,
  Collapse,
  Switch,
  TextField,
  CircularProgress,
  InputAdornment,
} from '@mui/material'
import {
  X,
  SlidersHorizontal,
  ChevronDown,
  ChevronRight,
  Search,
  Scale,
  Gavel,
  FileText,
  RotateCcw,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  setPreSearchFilters,
  clearPreSearchFilters,
  setFilterModalOpen,
  fetchFilterOptions,
} from '../../../redux/actions/searchAction'
import { useSearchTokens } from '../searchDesignTokens'
import ModeDropdown from '../../../components/common/ModeDropdown'

// ─── Filter Field Config ────────────────────────────────────────────────────

const LAW_FILTER_KEYS = [
  'legislation_type',
  'legal_domain',
  'issue_year_range',
  'language',
  'is_active',
]

const REGULATION_FILTER_KEYS = [
  'source_platform',
  'regulation_type',
  'legal_domain',
  'issue_year_range',
  'language',
]

const CASE_LAW_FILTER_KEYS = [
  'court',
  'court_level',
  'case_type_category',
  'decision_type',
  'court_division',
  'judgment_year_range',
  'language',
]

// ─── Sub-components ─────────────────────────────────────────────────────────

const CountBadge = ({ count, dk }) => (
  <Typography
    component="span"
    sx={{
      fontSize: '0.62rem',
      fontWeight: 600,
      color: dk.text3,
      backgroundColor: dk.chipBg,
      borderRadius: '10px',
      px: 0.7,
      py: '1px',
      ml: 'auto',
      minWidth: 18,
      textAlign: 'center',
      lineHeight: 1.6,
    }}
  >
    {count}
  </Typography>
)

const SectionHeader = ({ title, expanded, onToggle, selectedCount, dk }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      py: 1,
      px: 1.5,
      cursor: 'pointer',
      borderRadius: '8px',
      transition: 'background-color 0.15s',
      '&:hover': { backgroundColor: dk.hover },
    }}
  >
    {expanded ? (
      <ChevronDown size={14} color={dk.text3} />
    ) : (
      <ChevronRight size={14} color={dk.text3} />
    )}
    <Typography
      sx={{
        fontSize: '0.73rem',
        fontWeight: 600,
        color: dk.text2,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        flexGrow: 1,
      }}
    >
      {title}
    </Typography>
    {selectedCount > 0 && (
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          backgroundColor: dk.gold,
          color: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.58rem',
          fontWeight: 700,
        }}
      >
        {selectedCount}
      </Box>
    )}
  </Box>
)

const MultiSelectFilter = ({ title, options = [], selectedValues = [], onChange, dk }) => {
  const [expanded, setExpanded] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const showSearch = options.length > 6

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options
    const lower = searchTerm.toLowerCase()
    return options.filter(o => o.label?.toLowerCase().includes(lower))
  }, [options, searchTerm])

  const handleToggle = useCallback(
    (value) => {
      const next = selectedValues.includes(value)
        ? selectedValues.filter(v => v !== value)
        : [...selectedValues, value]
      onChange(next)
    },
    [selectedValues, onChange]
  )

  return (
    <Box>
      <SectionHeader
        title={title}
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
        selectedCount={selectedValues.length}
        dk={dk}
      />
      <Collapse in={expanded} timeout={200}>
        <Box sx={{ pb: 0.5 }}>
          {showSearch && (
            <Box sx={{ px: 1.5, pb: 0.75 }}>
              <TextField
                size="small"
                fullWidth
                placeholder={`Search...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={13} color={dk.text3} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 30,
                    fontSize: '0.72rem',
                    backgroundColor: dk.inputBg,
                    borderRadius: '6px',
                    '& fieldset': { borderColor: dk.border },
                    '&:hover fieldset': { borderColor: dk.borderHover },
                    '&.Mui-focused fieldset': { borderColor: dk.goldBdr },
                  },
                  '& .MuiInputBase-input': { color: dk.text1, py: 0.5 },
                }}
              />
            </Box>
          )}
          <Box sx={{ maxHeight: 180, overflowY: 'auto', px: 0.5 }}>
            {filteredOptions.map((opt) => (
              <Box
                key={opt.value}
                onClick={() => handleToggle(opt.value)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  py: 0.35,
                  px: 1,
                  cursor: 'pointer',
                  borderRadius: '6px',
                  transition: 'background-color 0.12s',
                  '&:hover': { backgroundColor: dk.hover },
                }}
              >
                <Checkbox
                  checked={selectedValues.includes(opt.value)}
                  size="small"
                  sx={{
                    p: 0.25,
                    color: dk.disabled,
                    '&.Mui-checked': { color: dk.gold },
                    '& .MuiSvgIcon-root': { fontSize: '1rem' },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    color: selectedValues.includes(opt.value) ? dk.text1 : dk.text2,
                    flexGrow: 1,
                    lineHeight: 1.4,
                  }}
                  noWrap
                >
                  {opt.label}
                </Typography>
                {opt.count != null && <CountBadge count={opt.count} dk={dk} />}
              </Box>
            ))}
            {filteredOptions.length === 0 && (
              <Typography sx={{ fontSize: '0.7rem', color: dk.text3, py: 1, textAlign: 'center' }}>
                —
              </Typography>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  )
}

const YearRangeFilter = ({ title, min, max, selectedMin, selectedMax, onChange, dk }) => {
  const [expanded, setExpanded] = useState(false)
  const hasValue = selectedMin != null || selectedMax != null

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      height: 32,
      fontSize: '0.72rem',
      backgroundColor: dk.inputBg,
      borderRadius: '6px',
      '& fieldset': { borderColor: dk.border },
      '&:hover fieldset': { borderColor: dk.borderHover },
      '&.Mui-focused fieldset': { borderColor: dk.goldBdr },
    },
    '& .MuiInputBase-input': {
      color: dk.text1,
      py: 0.5,
      textAlign: 'center',
    },
  }

  return (
    <Box>
      <SectionHeader
        title={title}
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
        selectedCount={hasValue ? 1 : 0}
        dk={dk}
      />
      <Collapse in={expanded} timeout={200}>
        <Box sx={{ display: 'flex', gap: 1, px: 1.5, pb: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            type="number"
            placeholder={String(min || '')}
            value={selectedMin ?? ''}
            onChange={(e) => {
              const v = e.target.value ? Number(e.target.value) : null
              onChange(v, selectedMax)
            }}
            sx={{ flex: 1, ...inputSx }}
          />
          <Typography sx={{ fontSize: '0.68rem', color: dk.text3 }}>—</Typography>
          <TextField
            size="small"
            type="number"
            placeholder={String(max || '')}
            value={selectedMax ?? ''}
            onChange={(e) => {
              const v = e.target.value ? Number(e.target.value) : null
              onChange(selectedMin, v)
            }}
            sx={{ flex: 1, ...inputSx }}
          />
        </Box>
      </Collapse>
    </Box>
  )
}

const ToggleFilter = ({ title, value, onChange, dk }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      py: 0.75,
      px: 1.5,
    }}
  >
    <Typography
      sx={{
        fontSize: '0.73rem',
        fontWeight: 600,
        color: dk.text2,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        flexGrow: 1,
      }}
    >
      {title}
    </Typography>
    <Switch
      checked={value ?? true}
      onChange={(e) => onChange(e.target.checked)}
      size="small"
      sx={{
        '& .MuiSwitch-switchBase.Mui-checked': { color: dk.gold },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: dk.goldBdr },
        '& .MuiSwitch-track': { backgroundColor: dk.border },
      }}
    />
  </Box>
)

// ─── Mode Filters Renderer ──────────────────────────────────────────────────

const FilterSection = ({ filterKeys, filterDefs, localFilters, setLocalFilter, dk, t }) => {
  if (!filterDefs) return null

  return filterKeys.map((key) => {
    const def = filterDefs[key]
    if (!def) return null

    if (def.type === 'multi_select') {
      return (
        <MultiSelectFilter
          key={key}
          title={def.label || t(`filter_${key}`) || key}
          options={def.options || []}
          selectedValues={localFilters[key] || []}
          onChange={(vals) => setLocalFilter(key, vals)}
          dk={dk}
        />
      )
    }

    if (def.type === 'year_range') {
      const yearVal = localFilters[key] || {}
      return (
        <YearRangeFilter
          key={key}
          title={def.label || t(`filter_${key}`) || key}
          min={def.min}
          max={def.max}
          selectedMin={yearVal.min}
          selectedMax={yearVal.max}
          onChange={(newMin, newMax) => {
            const next = {}
            if (newMin != null) next.min = newMin
            if (newMax != null) next.max = newMax
            setLocalFilter(key, Object.keys(next).length > 0 ? next : null)
          }}
          dk={dk}
        />
      )
    }

    if (def.type === 'toggle') {
      return (
        <ToggleFilter
          key={key}
          title={def.label || t(`filter_${key}`) || key}
          value={localFilters[key]}
          onChange={(val) => setLocalFilter(key, val)}
          dk={dk}
        />
      )
    }

    return null
  })
}

// ─── Main Component ─────────────────────────────────────────────────────────

const PreSearchFilterModal = ({ open, onClose, mode, setMode }) => {
  const dk = useSearchTokens()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const filterOptions = useSelector((state) => state.search.filterOptions)
  const filterOptionsLoading = useSelector((state) => state.search.filterOptionsLoading)
  const preSearchFilters = useSelector((state) => state.search.preSearchFilters) || {}
  const countryPref = useSelector((state) => state.user?.countryPreference)

  // Local state for in-progress filter selections
  const [localLawFilters, setLocalLawFilters] = useState({})
  const [localRegulationFilters, setLocalRegulationFilters] = useState({})
  const [localCaseLawFilters, setLocalCaseLawFilters] = useState({})

  const countries = Array.isArray(countryPref) ? countryPref : countryPref ? [countryPref] : ['MA']
  const firstCountry = countries[0]
  const onlyBF = countries.length === 1 && countries[0] === 'BF'

  // Fetch filter options when modal opens or mode/country changes
  useEffect(() => {
    if (open && firstCountry) {
      dispatch(fetchFilterOptions(mode, firstCountry))
    }
  }, [open, mode, firstCountry, dispatch])

  // Initialize local state from Redux when modal opens
  useEffect(() => {
    if (open) {
      setLocalLawFilters(preSearchFilters.law || {})
      setLocalRegulationFilters(preSearchFilters.regulation || {})
      setLocalCaseLawFilters(preSearchFilters.case_law || {})
    }
  }, [open, preSearchFilters])

  const setLawFilter = useCallback((key, value) => {
    setLocalLawFilters((prev) => {
      const next = { ...prev }
      if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
        delete next[key]
      } else {
        next[key] = value
      }
      return next
    })
  }, [])

  const setRegulationFilter = useCallback((key, value) => {
    setLocalRegulationFilters((prev) => {
      const next = { ...prev }
      if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
        delete next[key]
      } else {
        next[key] = value
      }
      return next
    })
  }, [])

  const setCaseLawFilter = useCallback((key, value) => {
    setLocalCaseLawFilters((prev) => {
      const next = { ...prev }
      if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
        delete next[key]
      } else {
        next[key] = value
      }
      return next
    })
  }, [])

  // Parse mode to check which types are active
  const activeModes = mode ? mode.split(',').map(m => m.trim()) : ['law']
  const hasLaw = activeModes.includes('law')
  const hasRegulation = activeModes.includes('regulation')
  const hasCaseLaw = activeModes.includes('case_law')
  const multiMode = activeModes.length > 1

  // Build the pre_filters shape the backend expects
  const buildPreFilters = useCallback(() => {
    const result = {}

    if (hasLaw && Object.keys(localLawFilters).length > 0) {
      const lf = {}
      if (localLawFilters.legislation_type?.length) lf.legislation_type_ids = localLawFilters.legislation_type
      if (localLawFilters.legal_domain?.length) lf.legal_domain_codes = localLawFilters.legal_domain
      if (localLawFilters.language?.length) lf.languages = localLawFilters.language
      if (localLawFilters.is_active !== undefined) lf.is_active = localLawFilters.is_active
      if (localLawFilters.issue_year_range?.min) lf.issue_year_min = localLawFilters.issue_year_range.min
      if (localLawFilters.issue_year_range?.max) lf.issue_year_max = localLawFilters.issue_year_range.max
      if (Object.keys(lf).length > 0) result.law = lf
    }

    if (hasRegulation && Object.keys(localRegulationFilters).length > 0) {
      const rf = {}
      if (localRegulationFilters.source_platform?.length) rf.source_platforms = localRegulationFilters.source_platform
      if (localRegulationFilters.regulation_type?.length) rf.regulation_type_ids = localRegulationFilters.regulation_type
      if (localRegulationFilters.legal_domain?.length) rf.legal_domain_codes = localRegulationFilters.legal_domain
      if (localRegulationFilters.language?.length) rf.languages = localRegulationFilters.language
      if (localRegulationFilters.issue_year_range?.min) rf.issue_year_min = localRegulationFilters.issue_year_range.min
      if (localRegulationFilters.issue_year_range?.max) rf.issue_year_max = localRegulationFilters.issue_year_range.max
      if (Object.keys(rf).length > 0) result.regulation = rf
    }

    if (hasCaseLaw && Object.keys(localCaseLawFilters).length > 0) {
      const cf = {}
      if (localCaseLawFilters.court?.length) cf.court_ids = localCaseLawFilters.court
      if (localCaseLawFilters.court_level?.length) cf.court_levels = localCaseLawFilters.court_level
      if (localCaseLawFilters.case_type_category?.length) cf.case_type_categories = localCaseLawFilters.case_type_category
      if (localCaseLawFilters.decision_type?.length) cf.decision_types = localCaseLawFilters.decision_type
      if (localCaseLawFilters.court_division?.length) cf.court_divisions = localCaseLawFilters.court_division
      if (localCaseLawFilters.language?.length) cf.languages = localCaseLawFilters.language
      if (localCaseLawFilters.judgment_year_range?.min) cf.judgment_year_min = localCaseLawFilters.judgment_year_range.min
      if (localCaseLawFilters.judgment_year_range?.max) cf.judgment_year_max = localCaseLawFilters.judgment_year_range.max
      if (Object.keys(cf).length > 0) result.case_law = cf
    }

    return result
  }, [hasLaw, hasRegulation, hasCaseLaw, localLawFilters, localRegulationFilters, localCaseLawFilters])

  const handleApply = () => {
    const filters = buildPreFilters()
    dispatch(setPreSearchFilters(filters))
    dispatch(setFilterModalOpen(false))
    onClose()
  }

  const handleClear = () => {
    setLocalLawFilters({})
    setLocalRegulationFilters({})
    setLocalCaseLawFilters({})
    dispatch(clearPreSearchFilters())
  }

  const handleModeChange = (newMode) => {
    setMode(newMode)
  }

  // Count active filters for the Apply button badge
  const activeFilterCount = useMemo(() => {
    let count = 0
    const countObj = (obj) =>
      Object.values(obj).reduce((acc, v) => {
        if (Array.isArray(v)) return acc + (v.length > 0 ? 1 : 0)
        if (v !== null && v !== undefined) return acc + 1
        return acc
      }, 0)
    if (hasLaw) count += countObj(localLawFilters)
    if (hasRegulation) count += countObj(localRegulationFilters)
    if (hasCaseLaw) count += countObj(localCaseLawFilters)
    return count
  }, [hasLaw, hasRegulation, hasCaseLaw, localLawFilters, localRegulationFilters, localCaseLawFilters])

  // Get the right filter definitions from the API response
  // Single mode → backend returns "filters", multi mode → returns "law_filters", "regulation_filters", etc.
  const currentLawDefs = multiMode ? (filterOptions?.law_filters || {}) : (hasLaw ? (filterOptions?.filters || {}) : {})
  const currentRegulationDefs = multiMode ? (filterOptions?.regulation_filters || {}) : (hasRegulation ? (filterOptions?.filters || {}) : {})
  const currentCaseLawDefs = multiMode ? (filterOptions?.case_law_filters || {}) : (hasCaseLaw ? (filterOptions?.filters || {}) : {})

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0,0,0,0.82)',
            backdropFilter: 'blur(12px)',
          },
        },
      }}
    >
      <Fade in={open} timeout={220}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95vw', sm: '88vw', md: '560px' },
            maxWidth: '96vw',
            maxHeight: '85vh',
            outline: 'none',
            borderRadius: '14px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: dk.surface,
            border: `1px solid ${dk.goldBdr}`,
            boxShadow: `0 40px 100px rgba(0,0,0,0.9), 0 0 0 1px ${dk.borderLight}`,
          }}
        >
          {/* ── Header ── */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: { xs: 2, md: 2.5 },
              py: 1.5,
              borderBottom: `1px solid ${dk.border}`,
              background: `linear-gradient(180deg, ${dk.elevated} 0%, ${dk.surface} 100%)`,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '8px',
                  backgroundColor: dk.goldSub,
                  border: `1px solid ${dk.goldBdr}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <SlidersHorizontal size={14} color={dk.gold} />
              </Box>
              <Typography
                sx={{
                  color: dk.text1,
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontSize: '1rem',
                  fontWeight: 500,
                  letterSpacing: '0.01em',
                }}
              >
                {t('advanced_filters')}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: dk.text3,
                width: 28,
                height: 28,
                '&:hover': {
                  color: dk.text1,
                  backgroundColor: dk.hover,
                },
              }}
            >
              <X size={15} />
            </IconButton>
          </Box>

          {/* ── Mode Selector ── */}
          <Box
            sx={{
              display: 'flex',
              gap: 0.75,
              px: 2.5,
              py: 1.25,
              borderBottom: `1px solid ${dk.border}`,
              backgroundColor: dk.bg,
              flexShrink: 0,
              alignItems: 'center',
            }}
          >
            <ModeDropdown
              value={mode}
              onChange={(e, newMode) => handleModeChange(newMode)}
              displayMode="icon"
              multiSelect
            />
          </Box>

          {/* ── Filter Body ── */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              py: 1,
              '&::-webkit-scrollbar': { width: 5 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: dk.scrollThumb,
                borderRadius: 4,
              },
            }}
          >
            {filterOptionsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={24} sx={{ color: dk.gold }} />
              </Box>
            ) : (
              <>
                {/* Law filters */}
                {hasLaw && (
                  <Box>
                    {multiMode && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 2.5,
                          pt: 1,
                          pb: 0.5,
                        }}
                      >
                        <Scale size={13} color={dk.gold} />
                        <Typography
                          sx={{
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            color: dk.gold,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                          }}
                        >
                          {t('legislation_filters')}
                        </Typography>
                      </Box>
                    )}
                    <FilterSection
                      filterKeys={LAW_FILTER_KEYS}
                      filterDefs={currentLawDefs}
                      localFilters={localLawFilters}
                      setLocalFilter={setLawFilter}
                      dk={dk}
                      t={t}
                    />
                  </Box>
                )}

                {/* Divider between sections in multi mode */}
                {hasLaw && (hasRegulation || hasCaseLaw) && (
                  <Box sx={{ mx: 2.5, my: 1, borderTop: `1px solid ${dk.border}` }} />
                )}

                {/* Regulation filters */}
                {hasRegulation && (
                  <Box>
                    {multiMode && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 2.5,
                          pt: 1,
                          pb: 0.5,
                        }}
                      >
                        <FileText size={13} color={dk.gold} />
                        <Typography
                          sx={{
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            color: dk.gold,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                          }}
                        >
                          {t('regulation_filters')}
                        </Typography>
                      </Box>
                    )}
                    <FilterSection
                      filterKeys={REGULATION_FILTER_KEYS}
                      filterDefs={currentRegulationDefs}
                      localFilters={localRegulationFilters}
                      setLocalFilter={setRegulationFilter}
                      dk={dk}
                      t={t}
                    />
                  </Box>
                )}

                {/* Divider before case law */}
                {hasRegulation && hasCaseLaw && (
                  <Box sx={{ mx: 2.5, my: 1, borderTop: `1px solid ${dk.border}` }} />
                )}

                {/* Case law filters */}
                {hasCaseLaw && (
                  <Box>
                    {multiMode && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 2.5,
                          pt: 1,
                          pb: 0.5,
                        }}
                      >
                        <Gavel size={13} color={dk.gold} />
                        <Typography
                          sx={{
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            color: dk.gold,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                          }}
                        >
                          {t('case_law_filters')}
                        </Typography>
                      </Box>
                    )}
                    <FilterSection
                      filterKeys={CASE_LAW_FILTER_KEYS}
                      filterDefs={currentCaseLawDefs}
                      localFilters={localCaseLawFilters}
                      setLocalFilter={setCaseLawFilter}
                      dk={dk}
                      t={t}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* ── Footer ── */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.5,
              py: 1.5,
              borderTop: `1px solid ${dk.border}`,
              backgroundColor: dk.bg,
              flexShrink: 0,
            }}
          >
            <Box
              onClick={handleClear}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                py: 0.5,
                px: 1,
                borderRadius: '6px',
                transition: 'all 0.15s',
                '&:hover': { backgroundColor: dk.hover },
              }}
            >
              <RotateCcw size={13} color={dk.text3} />
              <Typography
                sx={{
                  fontSize: '0.72rem',
                  color: dk.text3,
                  fontWeight: 500,
                  '&:hover': { color: dk.text2 },
                }}
              >
                {t('clear_all_filters')}
              </Typography>
            </Box>

            <Box
              onClick={handleApply}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                cursor: 'pointer',
                py: 0.65,
                px: 2,
                borderRadius: '8px',
                backgroundColor: dk.gold,
                color: '#000',
                transition: 'all 0.18s ease',
                '&:hover': {
                  backgroundColor: dk.chipBgActive,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 16px ${dk.goldBdr}`,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.74rem',
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                }}
              >
                {t('apply_filters')}
              </Typography>
              {activeFilterCount > 0 && (
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.6rem',
                    fontWeight: 700,
                  }}
                >
                  {activeFilterCount}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  )
}

export default PreSearchFilterModal
